'use client';
import 'client-only';

import type { UseQueryOptions } from '@zg-rentals/react-query5';
import { useQuery, useQueryClient } from '@zg-rentals/react-query5';
import { isEqual, omit } from 'es-toolkit';
import { type ComponentType, createContext, useContext, useEffect, useMemo, useRef } from 'react';
import type { AuthConfig } from '.';
import type { RedirectHref } from './legacy';

export type { RedirectHref } from './legacy';

/**
 * Create the React runtime (Provider, Boundary, withBoundary, useAuth) for an
 * `AuthConfig`. The runtime is purely client-side; server components should use
 * `prefetch` from `@zg-rentals/auth-react/server` instead.
 */
export function defineClient<Props extends {}, User extends {}>(config: AuthConfig<Props, User>) {
  const { queryOptions, isAuthorized } = config;

  const Context = createContext<{ options: UseQueryOptions<User>; props: Props } | null>(null);

  const Provider = (props: { children: React.ReactNode } & Props) => {
    const propsRef = useRef(props);
    if (!isEqual(omit(propsRef.current, ['children']), omit(props, ['children']))) {
      propsRef.current = props;
    }
    const stableProps = propsRef.current;

    const options = useMemo(() => queryOptions(stableProps), [stableProps]);
    const value = useMemo(() => ({ options, props: stableProps }), [options, stableProps]);
    useQuery(value.options);

    return <Context.Provider value={value}>{props.children}</Context.Provider>;
  };

  const TestProvider = (props: { children: React.ReactNode; user: User | null } & Props) => {
    const queryClient = useQueryClient();
    const propsRef = useRef(props);
    if (!isEqual(omit(propsRef.current, ['children']), omit(props, ['children']))) {
      propsRef.current = props;
    }
    const stableProps = propsRef.current;
    const providerProps = useMemo(() => omit(stableProps, ['children', 'user']) as unknown as Props, [stableProps]);

    const options = useMemo(
      () => ({
        ...queryOptions(providerProps),
        initialData: stableProps.user as User,
        queryFn: () => stableProps.user as User,
        retry: false,
        staleTime: Infinity,
      }),
      [providerProps, stableProps.user],
    );
    const value = useMemo(() => ({ options, props: providerProps }), [options, providerProps]);
    useQuery(value.options);

    useEffect(() => {
      if (value.options.queryKey) {
        queryClient.setQueryData(value.options.queryKey, stableProps.user);
      }
    }, [queryClient, stableProps.user, value.options.queryKey]);

    return <Context.Provider value={value}>{props.children}</Context.Provider>;
  };

  const useAuth = () => {
    const context = useContext(Context);
    if (!context) {
      throw new Error('useAuth must be called from a descendant of <Auth.Provider>');
    }

    const { data: user = null, error, ...rest } = useQuery(context.options);

    if (error) {
      return { user: null, isAuthorized: false, error, ...rest } as const;
    }

    if (user && isAuthorized(user, context.props)) {
      return { user, isAuthorized: true, error, ...rest } as const;
    }

    return { user, isAuthorized: false, error, ...rest } as const;
  };

  type BoundaryProps = {
    /**
     * Rendered when user is authorized
     */
    children: React.ReactNode | ((user: User) => React.ReactNode);

    /**
     * Rendered while user is being fetched/authorized
     */
    loading?: React.ReactNode;

    /**
     * Rendered while redirecting to `redirectHref`
     */
    redirecting?: React.ReactNode;

    /**
     * Produces a redirect `onUnauthorized`, use `params` to break redirect loops
     */
    redirectHref?: RedirectHref;

    /**
     * Search param key used to detect redirect loops. Its value is a
     * counter incremented on each redirect attempt; once the counter is
     * non-zero the Boundary stops redirecting and falls through to
     * `onUnauthorized`. Defaults to `'ap'`.
     */
    searchParam?: string;

    /**
     * Enhance the provider-level `isAuthorized` check for this boundary
     *
     * @note calls both the provider-level `isAuthorized` check and this boundary-level `isAuthorized` check
     */
    isAuthorized?: (user: User) => boolean;

    /**
     * Callback that fires when redirect completes. Note: wrapped in useRef so changes to the function
     * reference will not retrigger the effect.
     */
    onRedirectAuthorized?: () => void;

    /**
     * Rendered if there was an error fetching or authorizing user
     */
    onError?: React.ReactNode | ((reason: string) => React.ReactNode);

    /**
     * Rendered if user is unauthorized
     *
     * @note If `redirectHref` is provided, this is only called if user is unauthorized after a redirect
     */
    onUnauthorized?: React.ReactNode | ((user: User) => React.ReactNode);
  };

  const Boundary = ({
    children,
    loading = <>Loading...</>,
    redirecting = <>Redirecting...</>,
    redirectHref,
    searchParam = 'ap',
    isAuthorized: boundaryIsAuthorized = () => true,
    onRedirectAuthorized,
    onError,
    onUnauthorized,
  }: BoundaryProps) => {
    const handleError = (message: string) => (typeof onError === 'function' ? onError(message) : (onError ?? message));
    const stableOnRedirectAuthorized = useRef(onRedirectAuthorized);

    const handleUnauthorized = (unauthorizedUser: User) => {
      if (redirectHref && typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const count = Number(params.get(searchParam)) || 0;

        if (count === 0) {
          params.set(searchParam, String(count + 1));

          window.location.href = redirectHref(window.location.href.split('?')[0], params);
          return redirecting;
        }
      }

      return typeof onUnauthorized === 'function'
        ? onUnauthorized(unauthorizedUser)
        : (onUnauthorized ?? handleError('User not authorized'));
    };

    const { error, user, isAuthorized: providerAuthorized } = useAuth();
    const isBoundaryAuthorized = providerAuthorized && user != null && boundaryIsAuthorized(user);

    useEffect(() => {
      if (!isBoundaryAuthorized) {
        return;
      }

      const url = new URL(window.location.href);
      if (url.searchParams.has(searchParam)) {
        url.searchParams.delete(searchParam);
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
        stableOnRedirectAuthorized.current?.();
      }
    }, [isBoundaryAuthorized, searchParam]);

    if (error) {
      return handleError(error instanceof Error ? error.message : 'Unknown error');
    } else if (isBoundaryAuthorized) {
      return typeof children === 'function' ? children(user) : children;
    } else if (user != null) {
      return handleUnauthorized(user);
    } else {
      return loading;
    }
  };

  const withBoundary = <P extends { user: User }>(
    Component: ComponentType<P>,
    boundaryProps?: Omit<BoundaryProps, 'children'>,
  ) => {
    const Wrapped = (props: Omit<P, 'user'>) => (
      <Boundary {...boundaryProps}>{(user) => <Component {...(props as P)} user={user} />}</Boundary>
    );
    Wrapped.displayName = `withBoundary(${Component.displayName ?? Component.name ?? 'Component'})`;
    return Wrapped;
  };

  return {
    /**
     * Wrap around entire App to provide auth context to children
     */
    Provider,

    /**
     * Test utilities that replace `Auth.Provider` with seeded auth state. Do not nest inside `Auth.Provider`.
     */
    Test: {
      Provider: TestProvider,
    },

    /**
     * Wrap around a React sub-tree to ensure that user is logged in and
     * authorized before rendering children
     */
    Boundary,

    /**
     * HOC that wraps a component with a Boundary
     */
    withBoundary,

    /**
     * Access underlying user/authorized state directly
     */
    useAuth,
  } as const;
}
